import { parseURI, URITypeMap } from '@spotify-internal/uri';
import { FORM_CASES, FormValues } from './types';

// Transforms any URL with trail endings to URIs to avoid issues in backend
export const getURI = (uri: string) => {
  return parseURI(uri)?.toURI();
};

// Case values are required by enrichard endpoint
export const getCaseValue = (caseType: FORM_CASES) => {
  switch (caseType) {
    case FORM_CASES.ANOTHER_ARTIST_ON_MY_PAGE:
      return "Another artist's music is on my profile.";
    case FORM_CASES.MY_RELEASE_ON_ANOTHER_PAGE:
      return "Some of my music is on another artist's profile.";
    case FORM_CASES.MOVE_MY_RELEASE:
      return 'I need a release moved to a new artist page.';
    default:
      return 'Case unknown';
  }
};

export const getArtistUri = (caseType: FORM_CASES, formValues: FormValues) => {
  switch (caseType) {
    case FORM_CASES.MY_RELEASE_ON_ANOTHER_PAGE:
      return formValues.destinationArtistURI;
    case FORM_CASES.ANOTHER_ARTIST_ON_MY_PAGE:
      return formValues.myArtistURI;
    case FORM_CASES.MOVE_MY_RELEASE:
      return formValues.currentArtistURI;
    default:
      return '';
  }
};

export const getAlbumUris = (uris: string) => {
  return uris
    .trim()
    .split(/[ ,|;]+/)
    .map(albumUri => getURI(albumUri))
    .join();
};

export interface Page1Data {
  // Raw page-1 artist URI fields, passed through unswitched. Only one or two
  // of these are meaningfully populated for any given `problem` (case) — the
  // backend, which already knows how to interpret `problem`, picks whichever
  // one(s) it needs (e.g. for the CPOP credits lookup, and for the ticket's
  // artist_uri/other_artist_uri fields) rather than the frontend guessing.
  destinationArtistUri: string;
  incorrectArtistUri: string;
  myArtistUri: string;
  currentArtistUri: string;
  releaseUris: string[];
  artistName: string;
  problem: string;
  comments: string;
  date: string;
  email: string;
}

// Derives the page-2 inputData from the page-1 Formik values: a plain
// passthrough of the raw fields (URI-normalized), no per-case artist_uri
// selection — that decision belongs to whichever backend logic already
// knows how to interpret `problem`.
export const buildPage1Data = (
  formValues: FormValues,
  additionalComment?: string,
): Page1Data => {
  const releaseUris = formValues.albumURIs
    .trim()
    .split(/[ ,|;]+/)
    .map(uri => getURI(uri))
    .filter((uri): uri is string => Boolean(uri));
  const comments = additionalComment
    ? `${additionalComment}\n----\n${formValues.comments}`
    : formValues.comments;

  // Formik doesn't clear values from fields that are conditionally unmounted
  // when the user switches `case` (e.g. filling incorrectArtistURI, then
  // switching to a case that uses myArtistURI instead). Enrichard prioritizes
  // incorrect_artist_uri over the others, so a stale leftover value there
  // would win and enrich/submit against the wrong case — only pass through
  // the URI field(s) that actually apply to the selected case.
  const isSwapCase = formValues.case === FORM_CASES.MY_RELEASE_ON_ANOTHER_PAGE;
  const isAnotherArtistCase =
    formValues.case === FORM_CASES.ANOTHER_ARTIST_ON_MY_PAGE;
  const isMoveReleaseCase = formValues.case === FORM_CASES.MOVE_MY_RELEASE;

  return {
    destinationArtistUri: isSwapCase
      ? getURI(formValues.destinationArtistURI) ?? ''
      : '',
    incorrectArtistUri: isSwapCase
      ? getURI(formValues.incorrectArtistURI) ?? ''
      : '',
    myArtistUri: isAnotherArtistCase
      ? getURI(formValues.myArtistURI) ?? ''
      : '',
    currentArtistUri: isMoveReleaseCase
      ? getURI(formValues.currentArtistURI) ?? ''
      : '',
    releaseUris,
    artistName: isMoveReleaseCase ? formValues.artistName : '',
    problem: getCaseValue(formValues.case),
    comments,
    date: new Date().toUTCString(),
    email: formValues.email,
  };
};

export const validComment = (comments: string | undefined) => {
  if (!comments) {
    return true;
  }
  let valid = true;
  comments.split(' ').forEach(element => {
    if (
      parseURI(element)?.type === URITypeMap.ARTIST ||
      parseURI(element)?.type === URITypeMap.ALBUM
    ) {
      valid = false;
    }
  });
  return valid;
};
